import React, { useMemo } from 'react';

import { UNCATEGORIZED } from '../../stores/bedInventoryStore';

/**
 * B7 — organisation bed-inventory summary strip. ONE surface, reused on the
 * Bed & Tariff Setup builder AND the Bed Board, both fed by the ONE shared
 * `selectInventoryStats` selector (single source of truth — never re-derive a
 * count inline). CONFIG capacity only: copy says "beds configured", never any
 * occupancy word (available / occupied / vacant) — that arrives in EPIC C.
 *
 * @param {object} props.stats        selectInventoryStats(state) output
 * @param {object} [props.categoryById]  bed_category_id → category row (for chip names)
 * @param {string} [props.note]       optional caption (e.g. board filtered-vs-org label)
 */
export default function InventoryStatsStrip({ stats, categoryById = {}, note = null }) {
  const { totals, byCategory } = stats;

  // Resolve category ids → names; any UNCATEGORIZED / retired-unresolvable id
  // folds into one "Uncategorised" chip (beds never dropped, never fake-zeroed).
  const catChips = useMemo(() => {
    const named = [];
    let uncategorised = 0;
    for (const { category_id, count } of byCategory) {
      const cat = category_id !== UNCATEGORIZED ? categoryById[category_id] : null;
      if (cat && cat.name) named.push({ name: cat.name, count });
      else uncategorised += count;
    }
    named.sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
    if (uncategorised > 0) named.push({ name: 'Uncategorised', count: uncategorised });
    return named;
  }, [byCategory, categoryById]);

  return (
    <div
      className="rounded-xl border p-3 mb-3 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
      data-testid="inventory-stats-strip"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-800 dark:text-slate-100">
        <span>
          <strong data-testid="stat-beds">{totals.beds}</strong> beds configured
        </span>
        <span className="text-slate-400" aria-hidden="true">·</span>
        <span>
          <strong data-testid="stat-wards">{totals.wards}</strong> wards
        </span>
        <span className="text-slate-400" aria-hidden="true">·</span>
        <span>
          <strong data-testid="stat-rooms">{totals.rooms}</strong> rooms
        </span>
      </div>

      {catChips.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2" data-testid="stat-categories">
          {catChips.map((c) => (
            <span
              key={c.name}
              className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-300 dark:border-slate-600"
            >
              {c.count} {c.name}
            </span>
          ))}
        </div>
      )}

      {note && (
        <div className="mt-2 text-xs text-slate-500 dark:text-slate-400" data-testid="stat-note">
          {note}
        </div>
      )}
    </div>
  );
}
